import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SetTeamDefaultOrganizationPayload,
  SettingsRow,
  TeamDefaultOrganizationResponse,
} from '@upright/common/internal/settings/types';

async function getAllSettings() {
  return await fetchOrThrow<SettingsRow>(`/settings`, 'GET');
}

export function useGetAllSettings() {
  return useQuery({
    queryKey: ['ALL_SETTINGS'],
    queryFn: async () => await getAllSettings(),
    retry: 3,
  });
}

async function updateSettings(settings: Partial<SettingsRow>) {
  return await fetchOrThrow<SettingsRow>(`/settings`, 'PATCH', settings);
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<SettingsRow>) => {
      return await updateSettings(settings);
    },
    onSuccess: () => {
      void queryClient.refetchQueries({
        queryKey: ['ALL_SETTINGS'],
      });
    },
  });
}

const TEAM_DEFAULT_ORGANIZATION_QUERY_KEY = ['TEAM_DEFAULT_ORGANIZATION'];

export function useTeamDefaultOrganizationQuery() {
  return useQuery({
    queryKey: TEAM_DEFAULT_ORGANIZATION_QUERY_KEY,
    queryFn: async () => await fetchOrThrow<TeamDefaultOrganizationResponse>(`/settings/teamDefaultOrganization`),
  });
}

export function useSetTeamDefaultOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SetTeamDefaultOrganizationPayload) =>
      await fetchOrThrow<TeamDefaultOrganizationResponse>(`/settings/teamDefaultOrganization`, 'PUT', payload),
    onSuccess: async data => {
      queryClient.setQueryData(TEAM_DEFAULT_ORGANIZATION_QUERY_KEY, data);
      // The organization selector reads the setting from my-organizations, which is cached for the
      // session; without this the change would only reach it on the next sign-in.
      await queryClient.invalidateQueries({ queryKey: ['myOrganizations'] });
    },
  });
}
